import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import Hr from "@saleor/components/Hr";
import RadioSwitchField from "@saleor/components/RadioSwitchField";
import useDateLocalize from "@saleor/hooks/useDateLocalize";
import { ChangeEvent } from "@saleor/hooks/useForm";
import { UserError } from "@saleor/types";
import { getFieldError } from "@saleor/utils/errors";
import classNames from "classnames";
import React from "react";
import { useIntl } from "react-intl";

import { joinDateTime } from "../../misc";
import { DateContext } from "../Date/DateContext";
import FormSpacer from "../FormSpacer";
import DateVisibilitySelector from "./DateVisibilitySelector";

const useStyles = makeStyles(
  theme => ({
    checkbox: {
      alignItems: "flex-start",
      marginTop: 10
    },
    children: {
      "& button": {
        margin: "0 9px"
      },
      "& label": {
        marginTop: theme.spacing(2.5)
      }
    },
    date: {
      "& svg": {
        fill: theme.palette.primary.main
      },
      marginTop: theme.spacing(1),
      width: 170
    },
    time: {
      "& svg": {
        fill: theme.palette.primary.main
      },
      marginTop: theme.spacing(1),
      width: 110
    },
    label: {
      lineHeight: 1.2,
      marginBottom: 5,
      marginTop: 0
    },
    listingLabel: {
      marginTop: 9
    },
    secondLabel: {
      color: theme.palette.text.hint,
      fontSize: 12,
      marginBottom: theme.spacing(2)
    },
    switchField: {
      marginTop: theme.spacing(1)
    }
  }),
  { name: "VisibilityCard" }
);

interface Message {
  visibleLabel: string;
  hiddenLabel: string;
  visibleSecondLabel?: string;
  hiddenSecondLabel: string;
  availableLabel?: string;
  unavailableLabel?: string;
  availableSecondLabel?: string;
  setAvailabilityDateLabel?: string;
  preOrderLabel?: string;
  preOrderSecondLabel?: string;
  unPreOrderLabel?: string;
  setPreOrderDateLabel?: string;
}

export interface DateFields {
  publicationDate: string;
  availableForPurchaseDate?: string;
  availableForPurchaseTime?: string;
  preOrderDate?: string;
  preOrderTime?: string;
}

export interface VisibilityCardProps {
  children?: React.ReactNode | React.ReactNodeArray;
  data: DateFields & {
    isAvailableForPurchase?: boolean;
    isPublished: boolean;
    visibleInListings?: boolean;
    isPreOrder?: boolean;
  };
  errors: UserError[];
  disabled?: boolean;
  messages: Message;
  onChange: (event: ChangeEvent) => void;
}

export const VisibilityCard: React.FC<VisibilityCardProps> = props => {
  const {
    children,
    data: {
      availableForPurchaseDate,
      availableForPurchaseTime,
      isAvailableForPurchase: isAvailable,
      isPublished,
      publicationDate,
      visibleInListings,
      isPreOrder,
      preOrderDate,
      preOrderTime
    },
    errors,
    disabled,
    messages,
    onChange
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();
  const localizeDate = useDateLocalize();
  const dateNow = React.useContext(DateContext);
  const hasAvailableProps =
    isAvailable !== undefined &&
    availableForPurchaseDate !== undefined &&
    availableForPurchaseTime !== undefined;
  const hasPreOrderProps =
    isPreOrder !== undefined &&
    preOrderDate !== undefined &&
    preOrderTime !== undefined;
  const visibleMessage = (date: string) =>
    intl.formatMessage(
      {
        defaultMessage: "since {date}",
        description: "date"
      },
      {
        date: localizeDate(date, "L")
      }
    );

  const visibleTimeMessage = (date: string) =>
    intl.formatMessage(
      {
        defaultMessage: "since {date}",
        description: "date"
      },
      {
        date: localizeDate(date, "DD/MM/YYYY HH:mm")
      }
    );

  const handleRadioFieldChange = (type: keyof DateFields) => (
    e: ChangeEvent
  ) => {
    const { value } = e.target;
    if (!value) {
      onChange({
        target: {
          name: type,
          value: null
        }
      });
    }
    return onChange(e);
  };

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Visibility",
          description: "section header"
        })}
      />
      <CardContent>
        <RadioSwitchField
          disabled={disabled}
          error={!!getFieldError(errors, "isPublished")}
          firstOptionLabel={
            <>
              <p className={classes.label}>{messages.visibleLabel}</p>
              {isPublished &&
                publicationDate &&
                Date.parse(publicationDate) < dateNow && (
                  <span className={classes.secondLabel}>
                    {messages.visibleSecondLabel ||
                      visibleMessage(publicationDate)}
                  </span>
                )}
            </>
          }
          name={"isPublished" as keyof FormData}
          secondOptionLabel={
            <>
              <p className={classes.label}>{messages.hiddenLabel}</p>
              {publicationDate &&
                !isPublished &&
                Date.parse(publicationDate) >= dateNow && (
                  <span className={classes.secondLabel}>
                    {messages.hiddenSecondLabel}
                  </span>
                )}
            </>
          }
          value={isPublished}
          onChange={handleRadioFieldChange("publicationDate")}
        />
        {!isPublished && (
          <DateVisibilitySelector
            buttonText={intl.formatMessage({
              defaultMessage: "Set publication date"
            })}
            onInputClose={() =>
              onChange({ target: { name: "publicationDate", value: null } })
            }
          >
            <TextField
              error={!!getFieldError(errors, "publicationDate")}
              disabled={disabled}
              label={intl.formatMessage({
                defaultMessage: "Publish on",
                description: "publish on date"
              })}
              name="publicationDate"
              type="date"
              fullWidth={true}
              helperText={getFieldError(errors, "publicationDate")?.message}
              value={publicationDate ? publicationDate : ""}
              onChange={onChange}
              className={classes.date}
              InputLabelProps={{
                shrink: true
              }}
            />
          </DateVisibilitySelector>
        )}
        {getFieldError(errors, "isPublished") && (
          <>
            <FormSpacer />
            <Typography color="error">
              {getFieldError(errors, "isPublished")?.message}
            </Typography>
          </>
        )}
        {hasAvailableProps && (
          <>
            <Hr />
            <RadioSwitchField
              className={classes.switchField}
              disabled={disabled}
              error={!!getFieldError(errors, "isAvailableForPurchase")}
              firstOptionLabel={
                <>
                  <p className={classes.label}>{messages.availableLabel}</p>
                  {isAvailable &&
                    availableForPurchaseDate &&
                    Date.parse(
                      joinDateTime(
                        availableForPurchaseDate,
                        availableForPurchaseTime
                      )
                    ) < dateNow && (
                      <span className={classes.secondLabel}>
                        {visibleTimeMessage(
                          joinDateTime(
                            availableForPurchaseDate,
                            availableForPurchaseTime
                          )
                        )}
                      </span>
                    )}
                </>
              }
              name={"isAvailableForPurchase" as keyof FormData}
              secondOptionLabel={
                <>
                  <p className={classes.label}>{messages.unavailableLabel}</p>
                  {availableForPurchaseDate && !isAvailable && (
                    <span className={classes.secondLabel}>
                      {messages.availableSecondLabel}
                    </span>
                  )}
                </>
              }
              value={isAvailable}
              onChange={handleRadioFieldChange("availableForPurchaseDate")}
            />
            {!isAvailable && (
              <DateVisibilitySelector
                buttonText={messages.setAvailabilityDateLabel}
                onInputClose={() => {
                  onChange({
                    target: { name: "availableForPurchaseDate", value: null }
                  });
                  // onChange({
                  //   target: { name: "availableForPurchaseTime", value: null },
                  // })
                }}
              >
                <TextField
                  error={!!getFieldError(errors, "startDate")}
                  disabled={disabled}
                  label={intl.formatMessage({
                    defaultMessage: "Set available on",
                    description: "available on date"
                  })}
                  name="availableForPurchaseDate"
                  type="date"
                  fullWidth={true}
                  helperText={getFieldError(errors, "startDate")?.message}
                  value={
                    availableForPurchaseDate ? availableForPurchaseDate : ""
                  }
                  onChange={onChange}
                  className={classes.date}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                <TextField
                  disabled={disabled}
                  error={!!getFieldError(errors, "startDate")}
                  helperText={getFieldError(errors, "startDate")?.message}
                  name="availableForPurchaseTime"
                  onChange={onChange}
                  label={intl.formatMessage({
                    defaultMessage: "Set available on",
                    description: "available on time"
                  })}
                  value={
                    availableForPurchaseTime ? availableForPurchaseTime : ""
                  }
                  type="time"
                  InputLabelProps={{
                    shrink: true
                  }}
                  className={classes.time}
                  fullWidth={true}
                />
              </DateVisibilitySelector>
            )}
            {getFieldError(errors, "isAvailableForPurchase") && (
              <>
                <FormSpacer />
                <Typography color="error">
                  {getFieldError(errors, "isAvailableForPurchase")?.message}
                </Typography>
              </>
            )}
          </>
        )}
        {hasPreOrderProps && (
          <>
            <Hr />
            <RadioSwitchField
              className={classes.switchField}
              disabled={disabled}
              error={!!getFieldError(errors, "isPerOrder")}
              firstOptionLabel={
                <>
                  <p className={classes.label}>{messages.preOrderLabel}</p>
                  {isPreOrder &&
                    preOrderDate &&
                    Date.parse(joinDateTime(preOrderDate, preOrderTime)) >
                      dateNow && (
                      <span className={classes.secondLabel}>
                        {messages.preOrderSecondLabel}
                      </span>
                    )}
                </>
              }
              name={"isPreOrder" as keyof FormData}
              secondOptionLabel={
                <>
                  <p className={classes.label}>{messages.unPreOrderLabel}</p>
                  {!isPreOrder &&
                    preOrderDate &&
                    Date.parse(joinDateTime(preOrderDate, preOrderTime)) <
                      dateNow && (
                      <span className={classes.secondLabel}>
                        {visibleTimeMessage(
                          joinDateTime(preOrderDate, preOrderTime)
                        )}
                      </span>
                    )}
                </>
              }
              value={isPreOrder}
              onChange={handleRadioFieldChange("preOrderDate")}
            />
            {isPreOrder && (
              <DateVisibilitySelector
                buttonText={messages.setPreOrderDateLabel}
                onInputClose={() => {
                  onChange({
                    target: { name: "preOrderDate", value: null }
                  });
                }}
              >
                <TextField
                  error={!!getFieldError(errors, "preOrderDate")}
                  disabled={disabled}
                  label={intl.formatMessage({
                    defaultMessage: "Set pre order on"
                  })}
                  name="preOrderDate"
                  type="date"
                  fullWidth={true}
                  helperText={getFieldError(errors, "preOrderDate")?.message}
                  value={preOrderDate ? preOrderDate : ""}
                  onChange={onChange}
                  className={classes.date}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                <TextField
                  disabled={disabled}
                  error={!!getFieldError(errors, "preOrderDate")}
                  helperText={getFieldError(errors, "preOrderDate")?.message}
                  name="preOrderTime"
                  onChange={onChange}
                  label={intl.formatMessage({
                    defaultMessage: "Set pre order on"
                  })}
                  value={preOrderTime ? preOrderTime : ""}
                  type="time"
                  InputLabelProps={{
                    shrink: true
                  }}
                  className={classes.time}
                  fullWidth={true}
                />
              </DateVisibilitySelector>
            )}
            {getFieldError(errors, "isPreOrder") && (
              <>
                <FormSpacer />
                <Typography color="error">
                  {getFieldError(errors, "isPreOrder")?.message}
                </Typography>
              </>
            )}
          </>
        )}
        {visibleInListings !== undefined && (
          <>
            <Hr />
            <ControlledCheckbox
              className={classes.checkbox}
              name="visibleInListings"
              checked={visibleInListings}
              disabled={disabled}
              label={
                <>
                  <p
                    className={classNames(classes.label, classes.listingLabel)}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Show in product listings"
                    })}
                  </p>

                  <span className={classes.secondLabel}>
                    {intl.formatMessage({
                      defaultMessage:
                        "Disabling this checkbox will remove product from search and category pages. It will be available on collection pages."
                    })}
                  </span>
                </>
              }
              onChange={onChange}
            />
          </>
        )}
        <div className={classes.children}>{children}</div>
      </CardContent>
    </Card>
  );
};
VisibilityCard.displayName = "VisibilityCard";
export default VisibilityCard;
